import { injectable, inject } from 'inversify';
import Request from '@framework/api/Rpc/Request';
import withCache from '@framework/with-cache';
import { type IDispatcher } from '@users/json-rpc-dispatcher';
import DITypes from '@users/json-rpc-dispatcher/DITypes';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import { DealroadshowTenant } from '@drs/config/DealroadshowTenant';
import { EvercallTenant } from '@evercall/config/EvercallTenant';

export const INVESTOR_LOGIN_SSID_KEY = 'investor.login.ssid';

@injectable()
class SessionRepository {
  constructor(@inject(DITypes.JsonRpcDispatcher) private rpc: IDispatcher) {}

  /**
   * Will set cookie, if no cookie provided will try to finish sso login looking for ssoSsid cookie (back-end managed)
   */
  setSsidCookie = async (ssid?: string) => {
    const request = new Request('session.set_ssid_cookie', { ...(ssid && { ssid }) }, { ...(ssid && { ssid }) });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  /**
   * Create session for user
   */
  createUserSession = async (data: Object): Promise<{ ssid: string, redirectUrl?: string }> => {
    const request = new Request('session.login', data);
    const response = await this.rpc.call<RpcSuccess>(request);
    const result = response.getResult();

    return { ssid: result.ssid, redirectUrl: result.redirectUrl };
  };

  /**
   * Create session for user with special params(by entry code, by dashboard id, etc)
   */
  createCustomLogin = async (
    payload: {
      email: string,
    } & (
      | {
          tenant: DealroadshowTenant,
          params: {
            entryCode: string,
            verificationToken?: string,
          },
        }
      | {
          tenant: EvercallTenant,
          params:
            | {
                dashboardId: string,
                token: string | null,
              }
            | {
                conferenceId: string | null, // null is passed when user logs in to Evercall Group
              },
        }
    ),
  ): Promise<{ ssid: string }> => {
    const request = new Request('session.custom_login', payload);
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  };

  /**
   * Verify session for user
   */
  async verifySession() {
    const request = new Request('session.verify');
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * Deletes session
   */
  async invalidateSession() {
    const request = new Request('session.invalidate');
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * Prolongs session
   */
  async prolongSession({ tenant = undefined }: { tenant?: string }) {
    const request = new Request('session.prolong', { tenant });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult() as object;
  }

  /**
   * Get current session
   */
  @withCache
// @ts-ignore TODO: FIN-32798 strictNullChecks
  async getCurrentSession(ssid: string = null) {
// @ts-ignore TODO: FIN-32798 strictNullChecks
    const request = new Request('session.get_current_session', null, { ...(ssid && { ssid }) });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }

  /**
   * get current user from session
   */
  @withCache
// @ts-ignore TODO: FIN-32798 strictNullChecks
  async getCurrentUser(ssid: string = null) {
    const request = new Request('session.get_current_user', { aggregateCompany: true }, { ...(ssid && { ssid }) });
    const response = await this.rpc.call<RpcSuccess>(request);

    return response.getResult();
  }
}

export default SessionRepository;
